import type { Snapshot as PlayHQSnapshot } from '../data/snapshotTypes'
import type { Snapshot as Backup, Game, Player } from '../domain/types'
import { statType } from '../domain/statTypes'
import { periodLabel } from '../domain/periods'
import { resolveResult } from '../domain/results'

/**
 * Getting the data out.
 *
 * This is the only safety net there is. Everything lives in one browser's
 * IndexedDB, and Safari will evict site data it considers unused; an installed
 * home-screen app is far more durable but not guaranteed. So export has to work
 * even when the fancy path does not, which is why every route offers a clipboard
 * fallback: iOS in standalone mode has historically been unreliable about
 * downloads, and a file the scorer cannot retrieve is not a backup.
 */

export function backupFilename(prefix: string, ext: string, now = new Date()): string {
  const p = (n: number) => String(n).padStart(2, '0')
  const stamp = `${now.getFullYear()}${p(now.getMonth() + 1)}${p(now.getDate())}-${p(now.getHours())}${p(now.getMinutes())}`
  return `${prefix}-${stamp}.${ext}`
}

/** Returns false when the browser refused, so the caller can offer the clipboard. */
export function downloadText(filename: string, text: string, mime: string): boolean {
  try {
    const blob = new Blob([text], { type: `${mime};charset=utf-8` })
    const url = URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = filename
    a.rel = 'noopener'
    document.body.appendChild(a)
    a.click()
    a.remove()
    // Revoked late: Safari has been known to cancel an in-flight download when
    // the object URL disappears too soon.
    setTimeout(() => URL.revokeObjectURL(url), 30_000)
    return true
  } catch {
    return false
  }
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text)
    return true
  } catch {
    return false
  }
}

// --- CSV ---------------------------------------------------------------------

function csvCell(value: unknown): string {
  const s = value == null ? '' : String(value)
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
}

function csvRows(rows: unknown[][]): string {
  // CRLF and a UTF-8 BOM: Excel misreads plain LF and mangles accented names.
  return '﻿' + rows.map((r) => r.map(csvCell).join(',')).join('\r\n')
}

/**
 * One row per recorded stat. Deleted events are excluded -- an undo should not
 * reappear in a spreadsheet.
 */
export function eventsCsv(backup: Backup, snapshot: PlayHQSnapshot | null): string {
  const playersById = new Map(backup.players.map((p) => [p.id, p]))
  const gamesById = new Map(backup.games.map((g) => [g.id, g]))
  const opponentsById = new Map(backup.opponents.map((o) => [o.id, o]))

  const header = [
    'game_date', 'round', 'opponent', 'home_away', 'game_status', 'our_score', 'their_score', 'result',
    'counts_toward_ladder', 'player', 'playhq_profile_id', 'is_fill_in', 'stat', 'stat_code', 'period',
    'recorded_at', 'playhq_game_id',
  ]

  const rows: unknown[][] = [header]
  for (const e of backup.events) {
    if (e.deleted) continue
    const game = gamesById.get(e.gameId)
    if (!game) continue
    const player = playersById.get(e.playerId)
    const resolved = resolveResult(game, snapshot)
    const def = statType(e.statType)
    const format = snapshot?.season.periodFormat ?? 'halves'
    rows.push([
      game.date,
      game.roundAbbreviation ?? '',
      opponentName(game, opponentsById),
      game.homeAway,
      game.status,
      resolved.ourScore,
      resolved.theirScore,
      resolved.result ?? '',
      game.countsTowardLadder ?? '',
      player?.name ?? 'Unknown',
      player?.playhqProfileId ?? '',
      player?.isFillIn ? 'yes' : 'no',
      def.label,
      e.statType,
      periodLabel(format, e.period),
      new Date(e.at).toISOString(),
      game.playhqGameId ?? '',
    ])
  }
  return csvRows(rows)
}

/** One row per player per game: the shape most spreadsheets actually want. */
export function perGameCsv(backup: Backup, snapshot: PlayHQSnapshot | null): string {
  const playersById = new Map(backup.players.map((p) => [p.id, p]))
  const opponentsById = new Map(backup.opponents.map((o) => [o.id, o]))
  const stats = [...new Set(backup.events.filter((e) => !e.deleted).map((e) => e.statType))].sort()

  const header = ['game_date', 'round', 'opponent', 'home_away', 'result', 'player', ...stats.map((s) => statType(s).short.toLowerCase())]
  const rows: unknown[][] = [header]

  for (const game of [...backup.games].sort((a, b) => a.date.localeCompare(b.date))) {
    const resolved = resolveResult(game, snapshot)
    const events = backup.events.filter((e) => e.gameId === game.id && !e.deleted)
    for (const playerId of game.roster) {
      const player = playersById.get(playerId)
      const counts = stats.map((s) => events.filter((e) => e.playerId === playerId && e.statType === s).length)
      rows.push([
        game.date,
        game.roundAbbreviation ?? '',
        opponentName(game, opponentsById),
        game.homeAway,
        resolved.result ?? '',
        player?.name ?? 'Unknown',
        ...counts,
      ])
    }
  }
  return csvRows(rows)
}

function opponentName(game: Game, opponents: Map<string, { name: string }>): string {
  return game.opponentName ?? (game.opponentId ? (opponents.get(game.opponentId)?.name ?? '') : '')
}

// --- import ------------------------------------------------------------------

export interface ImportSummary {
  players: number
  opponents: number
  seasons: number
  games: number
  events: number
}

/**
 * Validates a backup before it is allowed anywhere near storage. Import merges
 * by id, so a malformed file could otherwise overwrite good records with junk.
 */
export function parseBackup(text: string): { backup: Backup; summary: ImportSummary } {
  let raw: unknown
  try {
    raw = JSON.parse(text)
  } catch {
    throw new Error('That is not valid JSON.')
  }
  if (!raw || typeof raw !== 'object') throw new Error('That file does not contain a backup.')
  const b = raw as Partial<Backup>
  if (b.version !== 1) throw new Error(`Unsupported backup version: ${String(b.version)}`)

  const arrays: (keyof ImportSummary)[] = ['players', 'opponents', 'seasons', 'games', 'events']
  for (const key of arrays) {
    if (!Array.isArray(b[key])) throw new Error(`Backup is missing its "${key}" list.`)
  }
  for (const list of arrays) {
    for (const row of b[list] as { id?: unknown }[]) {
      if (!row || typeof row.id !== 'string' || !row.id) {
        throw new Error(`A record in "${list}" has no id, so it cannot be merged safely.`)
      }
    }
  }

  const backup = b as Backup
  return {
    backup,
    summary: {
      players: backup.players.length,
      opponents: backup.opponents.length,
      seasons: backup.seasons.length,
      games: backup.games.length,
      events: backup.events.length,
    },
  }
}

/** Players who would be created rather than updated -- useful before importing. */
export function newPlayerCount(incoming: Player[], existing: Player[]): number {
  const ids = new Set(existing.map((p) => p.id))
  return incoming.filter((p) => !ids.has(p.id)).length
}
